<script lang="ts" setup>
import { computed } from 'vue';
import { formatTimeAgo } from '@vueuse/core';
import CollectionCatalogListColumns from '~/components/collections/CollectionCatalogListColumns.vue';
import CollectionKebabMenu from '~/components/collections/CollectionKebabMenu.vue';
import type { Collection } from '~/utilities/pyscript-api-models';
import { useUserStore } from '~/stores/user-store';
import { useCollectionsStore } from '~/stores/collections-store';
import IconLock from '~icons/material-symbols/lock';

const props = defineProps<{
  collection: Collection;
  lastItem: boolean;
}>();

defineEmits<{
  (e: 'collectionDeleted', value: Collection): void;
  (e: 'collectionUpdated', value: Collection): void;
}>();

const userStore = useUserStore();
const collectionsStore = useCollectionsStore();

const isCollectionOwner = computed(() => {
  if (!userStore.isLoggedIn) return false;
  return userStore.user?.id === props.collection.user_id;
});

const lastEdited = computed(() => {
  return formatTimeAgo(new Date(props.collection.updated_at));
});
</script>

<template>
  <CollectionCatalogListColumns
    class="group relative block px-2 text-13 hover:bg-new-gray-50 hover:dark:bg-new-gray-900"
    :class="[lastItem ? 'rounded-b' : 'border-b border-new-gray-50 dark:border-new-gray-950/60']"
  >
    <template #title>
      <div class="flex items-center gap-2">
        <router-link
          :to="{
            name: 'collection-projects',
            params: {
              collectionId: collection.id,
              usernameOrUserId: collection.username || collection.user_id,
            },
          }"
          class="truncate text-sm font-medium before:absolute before:inset-0 before:block"
        >
          {{ collection.name }}
        </router-link>

        <IconLock
          v-if="collection.private"
          v-tooltip="{
            placement: 'bottom',
            content: 'Private Project',
          }"
          class="shrink-0 text-xs text-new-blue-400"
        />
      </div>

      <div class="truncate text-xs text-black/60 dark:text-white/60 sm:hidden sm:text-13">
        {{ lastEdited }}
      </div>
    </template>

    <template #description>
      <div class="text-black/60 dark:text-white/60">
        <button
          v-if="!collection.description && isCollectionOwner"
          class="relative italic hover:underline"
          @click="collectionsStore.openCollectionModal(collection)"
        >
          Add a description...
        </button>

        <p v-else class="line-clamp-3 sm:line-clamp-none sm:truncate">{{
          collection.description
        }}</p>
      </div>
    </template>

    <template #count>
      <span
        class="block text-nowrap text-right text-xs text-black/60 dark:text-white/60 sm:text-left"
        >{{ $t('collections.project_count', collection.projects.length) }}</span
      >
    </template>

    <template #modified>
      <p class="truncate px-1 text-right">
        <span class="leading-tight text-black/60 dark:text-white/60">{{ lastEdited }}</span>
      </p>
    </template>

    <template #menu>
      <CollectionKebabMenu
        :collection="collection"
        @collection-deleted="$emit('collectionDeleted', $event)"
        @collection-updated="$emit('collectionUpdated', $event)"
      />
    </template>
  </CollectionCatalogListColumns>
</template>
